import { forwardRef } from 'react'
import { LocalizedString, useTranslate } from '../../TranslationsContext'
import { Container } from '../Container/Container'
import { Heading } from '../Heading/Heading'
import { BigNumber } from './BigNumber'
import './ProductSlide.css'
import { Button } from '../Button/Button'
import { SlideName } from './ProductsShowcase'
import productsImage360 from '../../assets/ceramol-acn3-products-360.jpg'
import productsImage640 from '../../assets/ceramol-acn3-products-640.jpg'
import productsImage960 from '../../assets/ceramol-acn3-products-960.jpg'
import productsImage from '../../assets/ceramol-acn3-products.jpg'
import spotBlockImage360 from '../../assets/ceramol-acn3-spot-block-360.png'
import spotBlockImage640 from '../../assets/ceramol-acn3-spot-block-640.png'
import spotBlockImage960 from '../../assets/ceramol-acn3-spot-block-960.png'
import spotBlockImage from '../../assets/ceramol-acn3-spot-block.png'
import actisebMousseImage360 from '../../assets/ceramol-acn3-actiseb-mousse-360.png'
import actisebMousseImage640 from '../../assets/ceramol-acn3-actiseb-mousse-640.png'
import actisebMousseImage960 from '../../assets/ceramol-acn3-actiseb-mousse-960.png'
import actisebMousseImage from '../../assets/ceramol-acn3-actiseb-mousse.png'
import rebalaneMatImage360 from '../../assets/ceramol-acn3-rebalance-mat-360.png'
import rebalaneMatImage640 from '../../assets/ceramol-acn3-rebalance-mat-640.png'
import rebalaneMatImage960 from '../../assets/ceramol-acn3-rebalance-mat-960.png'
import rebalaneMatImage from '../../assets/ceramol-acn3-rebalance-mat.png'

interface Props {
  name: SlideName
  currentSlide: SlideName
}

export const ProductSlide = forwardRef<HTMLDivElement, Props>((props, ref) => {
  const translate = useTranslate()
  const seeDetailsLabel = translate('Cta.seeDetails')
  const watchTutorialLabel = translate('Cta.videoTutorial')

  function getHeading(
    slideName: Exclude<SlideName, 'Products'>
  ): LocalizedString {
    switch (slideName) {
      case 'ActisebMousse':
        return translate('Products.ActisebMousse.name')
      case 'RebalanceMat':
        return translate('Products.RebalanceMat.name')
      case 'SpotBlock':
        return translate('Products.SpotBlock.name')
    }
  }

  function getDescription(
    slideName: Exclude<SlideName, 'Products'>
  ): LocalizedString {
    switch (slideName) {
      case 'ActisebMousse':
        return translate('Products.ActisebMousse.description')
      case 'RebalanceMat':
        return translate('Products.RebalanceMat.description')
      case 'SpotBlock':
        return translate('Products.SpotBlock.description')
    }
  }

  function getDetailsUrl(
    slideName: Exclude<SlideName, 'Products'>
  ): LocalizedString {
    switch (slideName) {
      case 'ActisebMousse':
        return translate('Products.ActisebMousse.detailsUrl')
      case 'RebalanceMat':
        return translate('Products.RebalanceMat.detailsUrl')
      case 'SpotBlock':
        return translate('Products.SpotBlock.detailsUrl')
    }
  }

  function getVideoTutorialUrl(
    slideName: Exclude<SlideName, 'Products'>
  ): LocalizedString {
    switch (slideName) {
      case 'ActisebMousse':
        return translate('Products.ActisebMousse.videoTutorialUrl')
      case 'RebalanceMat':
        return translate('Products.RebalanceMat.videoTutorialUrl')
      case 'SpotBlock':
        return translate('Products.SpotBlock.videoTutorialUrl')
    }
  }

  function getContent(slideName: SlideName): LocalizedString {
    switch (slideName) {
      case 'Products':
        return translate('Products.content')
      case 'ActisebMousse':
        return translate('Products.ActisebMousse.content')
      case 'RebalanceMat':
        return translate('Products.RebalanceMat.content')
      case 'SpotBlock':
        return translate('Products.SpotBlock.content')
    }
  }

  function getNumber(slideName: SlideName): number {
    switch (slideName) {
      case 'Products':
        return 3
      case 'ActisebMousse':
        return 1
      case 'RebalanceMat':
        return 2
      case 'SpotBlock':
        return 3
    }
  }

  return (
    <div
      className={`ProductSlide ${props.name}${
        props.currentSlide === props.name ? '' : ' hidden'
      }`}
      ref={ref}
    >
      {props.name === 'Products' ? (
        <div className="image">
          <img
            src={productsImage}
            srcSet={`${productsImage360} 360w, ${productsImage640} 640w, ${productsImage960} 960w, ${productsImage} 1920w`}
            alt="Ceramol Acn3"
            className={`products${
              props.currentSlide === 'Products' ? '' : ' hidden'
            }`}
          />
          <img
            src={actisebMousseImage}
            srcSet={`${actisebMousseImage360} 360w, ${actisebMousseImage640} 640w, ${actisebMousseImage960} 960w, ${actisebMousseImage} 1920w`}
            alt={getHeading('ActisebMousse')}
            className={props.currentSlide === 'ActisebMousse' ? '' : 'hidden'}
          />
          <img
            src={rebalaneMatImage}
            srcSet={`${rebalaneMatImage360} 360w, ${rebalaneMatImage640} 640w, ${rebalaneMatImage960} 960w, ${rebalaneMatImage} 1920w`}
            alt={getHeading('RebalanceMat')}
            className={props.currentSlide === 'RebalanceMat' ? '' : 'hidden'}
          />
          <img
            src={spotBlockImage}
            alt={getHeading('SpotBlock')}
            srcSet={`${spotBlockImage360} 360w, ${spotBlockImage640} 640w, ${spotBlockImage960} 960w, ${spotBlockImage} 1920w`}
            className={props.currentSlide === 'SpotBlock' ? '' : 'hidden'}
          />
        </div>
      ) : (
        <div className={`image`} />
      )}
      <Container>
        <BigNumber
          value={getNumber(props.name)}
          huge={props.name === 'Products'}
        />
        <div className="content">
          {props.name !== 'Products' && (
            <Heading type="Section" negative>
              {getHeading(props.name)}
            </Heading>
          )}
          {props.name !== 'Products' && (
            <Heading type="Description">{getDescription(props.name)}</Heading>
          )}
          <p>{getContent(props.name)}</p>
          {props.name !== 'Products' && (
            <div className="ctas">
              {seeDetailsLabel !== 'Cta.seeDetails' ? (
                <Button
                  type="Link"
                  url={getDetailsUrl(props.name)}
                  target="_blank"
                  label={seeDetailsLabel}
                  rel="noreferrer"
                />
              ) : null}
              {watchTutorialLabel !== 'Cta.videoTutorial' ? (
                <Button
                  type="Link"
                  url={getVideoTutorialUrl(props.name)}
                  target="_blank"
                  label={watchTutorialLabel}
                  rel="noreferrer"
                />
              ) : null}
            </div>
          )}
        </div>
      </Container>
    </div>
  )
})
